import type { IPlace } from "@bbj-types/address";
import type { IDocument } from "@bbj-types/document";
import type { IConfiguration, IOrganisation, ITemplatesOptions } from "@bbj-types/organisation";
import type { AppointmentReminderFeature } from "@bbj-types/organisation/enum";
import type { ITeam } from "@bbj-types/team";
import type { ObjectId, Override } from "@bbj-types/utils";
import { useSuspense } from "@root/api/tools/axios";
import { useCallback } from "react";
import { axiosInstance, axiosSettings } from "./axios";

export type TAppointmentReminderTemplates = Partial<
  Record<
    AppointmentReminderFeature,
    {
      sms?: { _id: string; name: string };
      email?: { _id: string; name: string };
    }
  >
>;

/** Parent organisation setup, absent when the organisation is a parent itself or when its parent configured nothing */
export type TParentAppointmentReminder = {
  organisationName: string;
  features: TAppointmentReminderTemplates;
};

export type IGetOrganisationResponse = Pick<IOrganisation, "_id" | "name" | "coachingStepRules"> & {
  cer: Override<
    Omit<IOrganisation["cer"], "apiKey" | "apiUrl" | "pdfTemplate">,
    {
      templatesOptions: Override<ITemplatesOptions, { templateId: IDocument }>[];
      renewalTemplate?: IDocument;
      reorientationTemplate?: IDocument;
    }
  >;
  places: IPlace;
  teams: Override<ITeam, { place: IPlace }>[];
  features: {
    team: { active: boolean; restrictedAccessActions: boolean };
    stonly: { active: boolean };
    google: { active: boolean };
    appointmentReminder?: TAppointmentReminderTemplates;
    export: { candidate: { limit: number; limitBeforeNotification: number } };
    coachingSummary: { candidateLimit: number };
  };
  configuration: Pick<IConfiguration, "helpCenterUrl">;
  parent?: ObjectId;
  parentAppointmentReminder?: TParentAppointmentReminder;
  fup?: { template?: IDocument };
};

export const useGetOrganisation = () => {
  const [state, execute, cancel] = axiosSettings<IGetOrganisationResponse, void, unknown>(
    {
      method: "GET",
      withCredentials: true,
    },
    { manual: true }
  );

  const callback = useCallback(
    (id: string) => {
      return execute({
        url: `/v2/organisations/${id}`,
      });
    },
    [execute]
  );

  return [state, callback, cancel] as const;
};

export const useGetOrganisationSuspense = (id: string) => {
  return useSuspense(
    () =>
      axiosInstance.get<IGetOrganisationResponse>(`/v2/organisations/${id}`, {
        withCredentials: true,
      }),
    [id]
  );
};
